import React, { useState } from "react";
import { NavLink } from "react-router-dom";
import { LogOut, X, Menu } from "lucide-react";

import { iconFor } from "../navIcons";

const SIDEBAR_W = 220;
const SIDEBAR_BG = "#0f172a";
const ACCENT = "#38bdf8";
const TEXT_MUTED = "rgba(148,163,184,0.85)";
const TEXT_ACTIVE = "#f1f5f9";
// Matches the nav links' own colour (TEXT_MUTED). Was rgba(100,116,139,0.7),
// which composites to #4b586e on the #0f172a sidebar — 2.48:1, below even the
// #94a3b8-on-white case the design system retires for failing WCAG AA. 5.37:1
// here. Uppercase, 700 weight and letter-spacing keep these distinct from links
// without relying on being dimmer.
const SECTION_LABEL_COLOR = "rgba(148,163,184,0.85)";

export interface NavItem {
  to?: string;
  label?: React.ReactNode;
  end?: boolean;
  section?: string;  // renders as a section divider/label
  /** Module identity colour (MODULE_COLORS), set only on the active module's
   *  section. Draws the same dot the module badge above it uses, so the header
   *  and the badge agree on which product you are inside. Core's own groups
   *  (People, System) have no dot, which is what separates them. */
  accent?: string;
}

interface AppNavProps {
  links: NavItem[];
  user: any;
  logout: () => void;
  companyName: string;
  logoUrl: string;
  LogoMark: React.ComponentType<{ logoUrl: string; companyName: string }>;
  /** Rendered directly under the logo — names the module the nav is scoped to. */
  moduleBadge?: React.ReactNode;
  /** Second mount of the launcher, for the mobile bar. The desktop header is
   *  display:none below 768px, so a header-only mount is unreachable there. */
  mobileSwitcher?: React.ReactNode;
}

function SidebarContent({
  links, user, logout, companyName, logoUrl, LogoMark, moduleBadge, mobileSwitcher, onLinkClick,
}: AppNavProps & { onLinkClick?: () => void }) {
  return (
    <div style={{
      display: "flex", flexDirection: "column", height: "100%",
      background: SIDEBAR_BG,
    }}>
      {/* ── Logo ── */}
      <div style={{
        padding: "18px 16px 14px",
        borderBottom: "1px solid rgba(255,255,255,0.05)",
        flexShrink: 0,
      }}>
        <NavLink to="/" end style={{ display: "flex", alignItems: "center", gap: 10, textDecoration: "none" }}>
          <LogoMark logoUrl={logoUrl} companyName={companyName} />
          <div style={{ minWidth: 0 }}>
            <div style={{
              color: TEXT_ACTIVE, fontWeight: 700, fontSize: 13.5,
              overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
            }}>
              {companyName}
            </div>
            {/* Product suffix only when the workspace has its own name —
                the unbranded default would render "HRxAI" twice. */}
            {companyName.trim().toLowerCase() !== "hrxai" && (
              <div style={{ color: ACCENT, fontSize: 10, fontWeight: 600, letterSpacing: 0.8, textTransform: "uppercase" }}>
                HRxAI
              </div>
            )}
          </div>
        </NavLink>
      </div>

      {moduleBadge}

      {/* ── Nav links ── */}
      <nav style={{ flex: 1, overflowY: "auto", padding: "8px 0", scrollbarWidth: "none" }}>
        {links.map((link, i) => {
          // Section header — a passive label, not a control. The sidebar is
          // scoped to one module at a time, so there is nothing here worth
          // folding away.
          if (link.section !== undefined) {
            return (
              <div key={`section-${i}`} style={{
                display: "flex", alignItems: "center", gap: 7,
                padding: "16px 16px 5px",
                fontSize: 10,
                fontWeight: 700,
                letterSpacing: 0.9,
                textTransform: "uppercase",
                color: SECTION_LABEL_COLOR,
                userSelect: "none",
              }}>
                {link.accent && (
                  <span aria-hidden style={{
                    width: 6, height: 6, borderRadius: "50%",
                    background: link.accent, flexShrink: 0,
                  }} />
                )}
                <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {link.section}
                </span>
              </div>
            );
          }

          if (!link.to) return null;
          const Icon = iconFor(link.to);

          return (
            <NavLink
              key={i}
              to={link.to}
              end={link.end}
              onClick={onLinkClick}
              style={({ isActive }: { isActive: boolean }) => ({
                display: "flex",
                alignItems: "center",
                gap: 9,
                padding: "8px 14px",
                margin: "1px 8px",
                borderRadius: 7,
                textDecoration: "none",
                fontSize: 13.5,
                fontWeight: isActive ? 600 : 400,
                color: isActive ? TEXT_ACTIVE : TEXT_MUTED,
                background: isActive ? "rgba(56,189,248,0.10)" : "transparent",
                borderLeft: isActive ? `3px solid ${ACCENT}` : "3px solid transparent",
                paddingLeft: isActive ? 11 : 14,
                transition: "background 0.12s, color 0.12s, border-color 0.12s",
              })}
            >
              {({ isActive }: { isActive: boolean }) => (
                <>
                  <Icon size={15} color={isActive ? ACCENT : TEXT_MUTED} strokeWidth={isActive ? 2.2 : 1.8} />
                  <span style={{ flex: 1 }}>{link.label}</span>
                </>
              )}
            </NavLink>
          );
        })}
      </nav>

      {/* ── User block ── */}
      <div style={{
        borderTop: "1px solid rgba(255,255,255,0.05)",
        padding: "12px 12px",
        flexShrink: 0,
      }}>
        <div style={{
          display: "flex", alignItems: "center", gap: 9,
          padding: "8px 10px", borderRadius: 8,
          background: "rgba(255,255,255,0.04)",
          marginBottom: 6,
        }}>
          {user?.picture ? (
            <img src={`${process.env.REACT_APP_API_URL || "http://localhost:30801"}${user.picture}`} alt="" style={{
              width: 30, height: 30, borderRadius: "50%", objectFit: "cover",
              border: "1.5px solid rgba(56,189,248,0.3)", flexShrink: 0,
            }} />
          ) : (
            <div style={{
              width: 30, height: 30, borderRadius: "50%", flexShrink: 0,
              background: "linear-gradient(135deg, #1e3a8a, #0284c7)",
              display: "flex", alignItems: "center", justifyContent: "center",
              color: "#f0f9ff", fontWeight: 700, fontSize: 12,
            }}>
              {(user?.name || "U")[0].toUpperCase()}
            </div>
          )}
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{
              color: TEXT_ACTIVE, fontSize: 12.5, fontWeight: 600,
              overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
            }}>
              {user?.name || "User"}
            </div>
            <div style={{
              color: TEXT_MUTED, fontSize: 10.5,
              overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
            }}>
              {user?.email || user?.role || ""}
            </div>
          </div>
        </div>
        <button
          onClick={logout}
          style={{
            display: "flex", alignItems: "center", gap: 7,
            width: "100%", padding: "6px 10px", borderRadius: 6,
            background: "transparent",
            border: "1px solid rgba(255,255,255,0.07)",
            color: "rgba(100,116,139,0.8)", fontSize: 12, cursor: "pointer",
            transition: "background 0.12s, color 0.12s",
          }}
          onMouseEnter={e => {
            e.currentTarget.style.background = "rgba(239,68,68,0.08)";
            e.currentTarget.style.color = "#f87171";
            e.currentTarget.style.borderColor = "rgba(239,68,68,0.25)";
          }}
          onMouseLeave={e => {
            e.currentTarget.style.background = "transparent";
            e.currentTarget.style.color = "rgba(100,116,139,0.8)";
            e.currentTarget.style.borderColor = "rgba(255,255,255,0.07)";
          }}
        >
          <LogOut size={12} />
          Sign out
        </button>
      </div>
    </div>
  );
}

export default function AppNav(props: AppNavProps) {
  const [mobileOpen, setMobileOpen] = useState(false);

  return (
    <>
      {/* ── Desktop sidebar (fixed left) ── */}
      <aside role="navigation" aria-label="Sidebar navigation" style={{
        position: "fixed", top: 0, left: 0, bottom: 0,
        width: SIDEBAR_W,
        zIndex: 200,
        display: "none",
      }} className="sidebar-desktop">
        <SidebarContent {...props} />
      </aside>

      {/* ── Mobile: top bar + drawer ── */}
      <nav role="navigation" aria-label="Main navigation" className="sidebar-mobile-bar" style={{
        display: "none",
        position: "fixed", top: 0, left: 0, right: 0, height: 56,
        background: SIDEBAR_BG,
        borderBottom: "1px solid rgba(255,255,255,0.06)",
        zIndex: 200,
        alignItems: "center",
        padding: "0 16px",
        gap: 12,
      }}>
        <button
          onClick={() => setMobileOpen(o => !o)}
          style={{
            background: "none", border: "none", color: TEXT_MUTED,
            cursor: "pointer", padding: 4, lineHeight: 1,
          }}
        >
          {mobileOpen ? <X size={22} /> : <Menu size={22} />}
        </button>

        {/* Launcher, mobile mount — the desktop header is hidden at this width */}
        {props.mobileSwitcher}
        <NavLink to="/" end style={{ display: "flex", alignItems: "center", gap: 8, textDecoration: "none" }}>
          <props.LogoMark logoUrl={props.logoUrl} companyName={props.companyName} />
          <span style={{ color: TEXT_ACTIVE, fontWeight: 700, fontSize: 14 }}>{props.companyName}</span>
          {props.companyName.trim().toLowerCase() !== "hrxai" && (
            <span style={{ color: ACCENT, fontSize: 11, fontWeight: 600 }}>· HRxAI</span>
          )}
        </NavLink>
      </nav>

      {/* Mobile drawer */}
      {mobileOpen && (
        <>
          <div
            onClick={() => setMobileOpen(false)}
            style={{
              position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)",
              zIndex: 299, display: "none",
            }}
            className="sidebar-mobile-overlay"
          />
          <aside style={{
            position: "fixed", top: 56, left: 0, bottom: 0,
            width: SIDEBAR_W, zIndex: 300,
            display: "none",
          }} className="sidebar-mobile-drawer">
            <SidebarContent {...props} onLinkClick={() => setMobileOpen(false)} />
          </aside>
        </>
      )}
    </>
  );
}

export const SIDEBAR_WIDTH = SIDEBAR_W;
